import { FC, useEffect, useMemo, useState } from 'react';
import Breadcrumbs from './components/Breadcrumbs';
import { FileDownload } from '@mui/icons-material';
import FilterRow from './components/FilterRow';
import HighlightCards from './components/HighlightCards';
import TopJobRoles from './components/TopJobRoles';
import { useFilter } from './contexts/FilterContext';


type TimeRange = "3 Months" | "6 Months" | "1 Year" | "3 Years";

type Props = {
    onBack: () => void;
};

const LabourForecastJobRoles: FC<Props> = ({ onBack }) => {
  const { selectedSector, selectedOccupation, timeRange, selectedTab } = useFilter();
const [sectors, setSectors] = useState<any[]>([]);
const [occupations, setOccupations] = useState<any[]>([]);


  useEffect(() => {
  const loadData = async () => {
    try {
      const sectorsData = await import(`./json/${selectedTab}/sectors.json`);
      const occupationsData = await import(`./json/${selectedTab}/occupations.json`);

      setSectors(sectorsData.default || []);
      setOccupations(occupationsData.default || []);
    } catch (error) {
      console.error(`Failed to load JSON for ${selectedTab}`, error);
      setSectors([]);
      setOccupations([]);
    }
  };

  loadData();
}, [selectedTab]);

    const highlightCards = useMemo(() => {
            let totalEmployed = 0;
            let distinctRoles = 0;
            let avgGrowth = 0;

            const isSectorAll = !selectedSector || selectedSector === 'All';

            if (isSectorAll) {
                const nonAllSectors = sectors.filter(s => s.code !== 'All');

                totalEmployed = nonAllSectors.reduce((sum, s) => sum + (s.total_employed || 0), 0);
                distinctRoles = nonAllSectors.reduce((sum, s) => sum + (s.distinct_roles || 0), 0);

                const totalGrowth = nonAllSectors.reduce((sum, s) => {
                return sum + (s.average_growth_rate?.[timeRange as TimeRange] || 0);
                }, 0);

                avgGrowth = nonAllSectors.length > 0 ? totalGrowth / nonAllSectors.length : 0;
            } else {
                const selected = sectors.find(s => s.code === selectedSector);
                totalEmployed = selected?.total_employed || 0;
                distinctRoles = selected?.distinct_roles || 0;
                avgGrowth = selected?.average_growth_rate?.[timeRange as TimeRange] || 0;
            }


        return [
            {
                icon: <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M35.9996 37.4383C36.4946 37.4792 36.9953 37.5 37.5009 37.5C39.5976 37.5 41.6105 37.1415 43.4818 36.4825C43.4944 36.3233 43.5009 36.1624 43.5009 36C43.5009 32.6863 40.8146 30 37.5009 30C36.2457 30 35.0805 30.3855 34.1172 31.0445M35.9996 37.4383C35.9997 37.4588 35.9998 37.4794 35.9998 37.5C35.9998 37.95 35.975 38.3942 35.9267 38.8313C32.4132 40.8473 28.3411 42 23.9998 42C19.6584 42 15.5863 40.8473 12.0728 38.8313C12.0245 38.3942 11.9998 37.95 11.9998 37.5C11.9998 37.4795 11.9998 37.459 11.9999 37.4385M35.9996 37.4383C35.9878 35.0852 35.2986 32.8922 34.1172 31.0445M34.1172 31.0445C31.9854 27.7104 28.2507 25.5 23.9998 25.5C19.7493 25.5 16.0151 27.7098 13.8831 31.0432M13.8831 31.0432C12.9202 30.385 11.7557 30 10.5012 30C7.18751 30 4.50122 32.6863 4.50122 36C4.50122 36.1624 4.50767 36.3233 4.52034 36.4825C6.39161 37.1415 8.40455 37.5 10.5012 37.5C11.0059 37.5 11.5057 37.4792 11.9999 37.4385M13.8831 31.0432C12.7012 32.8913 12.0117 35.0848 11.9999 37.4385M29.9998 13.5C29.9998 16.8137 27.3135 19.5 23.9998 19.5C20.686 19.5 17.9998 16.8137 17.9998 13.5C17.9998 10.1863 20.686 7.5 23.9998 7.5C27.3135 7.5 29.9998 10.1863 29.9998 13.5ZM41.9998 19.5C41.9998 21.9853 39.985 24 37.4998 24C35.0145 24 32.9998 21.9853 32.9998 19.5C32.9998 17.0147 35.0145 15 37.4998 15C39.985 15 41.9998 17.0147 41.9998 19.5ZM14.9998 19.5C14.9998 21.9853 12.985 24 10.4998 24C8.01447 24 5.99976 21.9853 5.99976 19.5C5.99976 17.0147 8.01447 15 10.4998 15C12.985 15 14.9998 17.0147 14.9998 19.5Z" stroke="#043C5B" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>,
                title: totalEmployed.toLocaleString(),
                subtitle: 'Total People Employed',
            },
            {
                icon: <svg xmlns="http://www.w3.org/2000/svg" width="49" height="48" viewBox="0 0 49 48" fill="none">
                    <path d="M41.1667 28.2998V36.7999C41.1667 38.9889 39.5929 40.8718 37.423 41.1599C33.2496 41.714 28.9915 42 24.6667 42C20.3418 42 16.0838 41.714 11.9104 41.1599C9.74048 40.8718 8.16669 38.9888 8.16669 36.7999V28.2998M41.1667 28.2998C42.1105 27.4953 42.6667 26.2778 42.6667 24.9778V17.4114C42.6667 15.2495 41.1309 13.3816 38.9928 13.0617C36.742 12.7249 34.4657 12.4663 32.1667 12.2886M41.1667 28.2998C40.7795 28.6299 40.327 28.8905 39.8209 29.0588C35.0573 30.6425 29.9622 31.5 24.6667 31.5C19.3713 31.5 14.2761 30.6425 9.51246 29.0587C9.00637 28.8905 8.5539 28.6299 8.16669 28.2998M8.16669 28.2998C7.22284 27.4952 6.66669 26.2777 6.66669 24.9777V17.4114C6.66669 15.2495 8.20249 13.3816 10.3406 13.0617C12.5913 12.7249 14.8677 12.4663 17.1667 12.2886M32.1667 12.2886V10.5C32.1667 8.01472 30.152 6 27.6667 6H21.6667C19.1814 6 17.1667 8.01472 17.1667 10.5V12.2886M32.1667 12.2886C29.6918 12.0974 27.1906 12 24.6667 12C22.1428 12 19.6416 12.0974 17.1667 12.2886M24.6667 25.5H24.6817V25.515H24.6667V25.5Z" stroke="#043C5B" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>,
                title: distinctRoles.toString(),
                subtitle: 'Distinct Job Roles',
            },
            {
                icon: avgGrowth >= 0 ? <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none">
                    <path d="M25.8666 14.6666H40.8M40.8 14.6666V29.6M40.8 14.6666L25.8666 29.6L18.4 22.1333L7.19995 33.3333" stroke="#043C5B" strokeWidth="3.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg> : <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M25.8669 33.3333H40.8002M40.8002 33.3333V18.4M40.8002 33.3333L25.8669 18.4L18.4002 25.8666L7.2002 14.6666" stroke="#043C5B" strokeWidth="3.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>,
                title: `${avgGrowth >= 0 ? '+' : ''}${avgGrowth.toFixed(1)}%`,
                subtitle: 'Average Role Growth Rate',
            },
        ];
    }, [selectedSector, selectedOccupation, sectors, timeRange]);



    return (
        <div className="labourForecast-jobRole px-4 py-3">
            <Breadcrumbs
                items={[
                    { label: 'Forecast Dashboard', onClick: onBack },
                    { label: 'View All Jobs' },
                ]}
            />
            <div className="container-fluid pb-3 mb-3 px-0 d-flex justify-content-between align-items-center rounded" style={{ borderBottom: '1px solid #E9EDF2' }}>
                <div>
                    <h2 className="mb-1 text-dark fw-semibold">Job Role Deep Dive</h2>
                    <p className="mb-0 text-secondary">Explore role-level employment trends, skill requirements, and growth patterns to support evidence-based policy and workforce planning.</p>
                </div>
                <button type="button" className="btn d-flex align-items-center gap-2 bg-white text-dark border rounded-pill" style={{ borderColor: '#D7DAE0' }}>
                    <FileDownload style={{ width: 20 }} />
                    Download Report
                </button>
            </div>
            <div className="container-fluid pb-3 px-0">
                <FilterRow sectors={sectors} occupations={occupations} />
            </div>
            <div className="container-fluid pb-3 px-0">
                <HighlightCards cards={highlightCards} />
            </div>
            <div className="container-fluid pb-3 px-0">
                <TopJobRoles />
            </div>
        </div>
    );
};

export default LabourForecastJobRoles;
